/**
 * Group bursts of related activity events (an alarm-off + a door + an egress
 * button within a minute) into one collapsible entry.
 *
 * Rule (shared by the dashboard's Recent Activity and the Timeline page):
 * - Same SITE only. The event payload carries no site, but both lists are
 *   already scoped to the active site, so the default treats every event as
 *   the same site; pass `getSite` when a list mixes sites. Floor/address are
 *   deliberately ignored — a walk-through crosses addresses.
 * - Chained in time: each event is within `gapMs` (2 min) of the previous
 *   event in its group, and the whole group spans at most `maxSpanMs` (10 min).
 * - Per person: events with different known actors never share a group.
 *   Actor-less events (alarm zone, egress button) join the nearest open group
 *   in time; an actor-less group is adopted by the first actor that joins it.
 * - Same place: an actor-less event joins another cluster — a person's group
 *   or another actor-less cluster alike — only when it happened at the same
 *   door/device (`deviceName`) or the same `location` as at least one event
 *   already in it; a person adopting an actor-less cluster needs the same
 *   match. An actor-less event that carries no place of its own (neither
 *   field set) has nothing to contradict, so it still joins the nearest
 *   actor-less cluster. A schedule arming L34 F7 is not absorbed into Clara
 *   walking through the L26 entrance three seconds later, and two actor-less
 *   alarms at different doors (L26 Floor 6 vs L22 Floor 4) no longer merge
 *   just because both are Axema.
 * - Same source only, unless a person ties it together: an actor-less event joins
 *   a group only when an event of the same producing app (`kind.app`) is already
 *   in it (an Axema zone + egress with the person's Axema access), and an actor
 *   adopts an actor-less group only of an app they also produced. A burst of
 *   calendar booking cancellations never lands in someone's Wi-Fi login burst.
 *   Events without a known app keep the old, app-blind behaviour.
 * - Device state changes stand alone: an actor-less event from an IoT source
 *   (`kind.app === "iot"`: alarm armed, pre-alarm, a sensor) is a schedule or a
 *   sensor, not someone walking through. It groups only with repeats of the very
 *   same thing (same title, location and device), and nothing else joins that
 *   group. So a pre-alarm then the alarm are separate rows. Override with
 *   `isRepeatsOnly`.
 * - One alarm zone, many floors: a repeats-only event that names its producing
 *   source (`sourceKey`, e.g. `iot.source:26`) groups by (source, state title
 *   without the location part) instead. The iot app writes one event PER LINKED
 *   FLOOR on one zone change ("Alarm: Disarmed — L26 › Floor 5", "… Floor 6",
 *   …): those become ONE group, its `zone` summary listing the floors. Another
 *   source (pre-alarm vs alarm, another zone) or another state is another group.
 *   Without a `sourceKey` the same-title/location/device rule above applies.
 * - Chains (optional): events with the same `getChainKey` (e.g. one booking's request and approval) join one
 *   group whoever acted, as long as it spans at most `maxSpanMs`; the `gapMs` rule does not apply between them.
 * - Headline: in a group with a known actor, the most significant of the
 *   actors' OWN events (severity, then latest); an actor-less member never takes
 *   the headline of a person's group. Without an actor: the most significant
 *   member overall.
 * - A group needs >= 2 events; everything else comes back as a single.
 *
 * Pure and order-independent: input may be in any order, output is newest
 * first (by each item's latest event), like the lists that render it.
 */

export interface GroupableEvent {
  id: number;
  /** ISO timestamp or Date. */
  time: string | Date;
  severity: string;
  title: string;
  actorName?: string | null;
  /** Where/what: tells repeats-only events apart (see the rule above). */
  location?: string | null;
  deviceName?: string | null;
  /** The producing app; `iot` makes an actor-less event repeats-only. */
  kind?: { app?: string | null } | null;
  /** The producing source object, e.g. `iot.source:26` (backend `source_key`): one alarm zone. */
  sourceKey?: string | null;
  /** That source's display name (the zone), when the API sends one. */
  sourceName?: string | null;
}

/** A group that is one source's state change seen at several places (an alarm zone across floors). */
export interface EventGroupZone {
  sourceKey: string;
  /** The zone's name, from the first member that carries one. */
  sourceName: string | null;
  /** The state, without the location part, e.g. `Alarm: Disarmed`. */
  state: string;
  /** Distinct locations (floors), oldest first. */
  locations: string[];
}

export interface SingleEventItem<E extends GroupableEvent> {
  kind: 'single';
  key: string;
  event: E;
}

export interface EventGroupItem<E extends GroupableEvent> {
  kind: 'group';
  /** Stable across re-groupings: keyed on the group's earliest event. */
  key: string;
  /** Members, newest first. */
  events: E[];
  /** The most significant member (by severity, then latest). */
  headline: E;
  /** Distinct known actor names, in order of first appearance. */
  actors: string[];
  start: Date;
  end: Date;
  /** Set when the group is one source's state across several places (see the rule above). */
  zone?: EventGroupZone;
}

export type EventListItem<E extends GroupableEvent> = SingleEventItem<E> | EventGroupItem<E>;

export interface GroupEventsOptions<E extends GroupableEvent> {
  gapMs?: number;
  maxSpanMs?: number;
  getSite?: (event: E) => string | number | null | undefined;
  /** Events with the same non-empty key belong together whoever acted (within `maxSpanMs`). Omitted = as before. */
  getChainKey?: (event: E) => string | null | undefined;
  /** Events that group only with exact repeats of themselves. Default: actor-less IoT events. */
  isRepeatsOnly?: (event: E) => boolean;
}

export const DEFAULT_GAP_MS = 2 * 60 * 1000;
export const DEFAULT_MAX_SPAN_MS = 10 * 60 * 1000;

const SEVERITY_RANK: Record<string, number> = {
  critical: 4,
  error: 3,
  warning: 2,
  success: 1,
  info: 0,
};

const severityRank = (severity: string): number => SEVERITY_RANK[severity?.toLowerCase()] ?? 0;

const actorOf = (event: GroupableEvent): string => (event.actorName ?? '').trim();

const LOCATION_SEPARATOR = ' — ';

/** The title without its trailing ` — <location>` part: `Alarm: Disarmed — L26 › Floor 5` → `Alarm: Disarmed`. */
export function stateTitle(event: Pick<GroupableEvent, 'title' | 'location'>): string {
  const title = (event.title ?? '').trim();
  const location = (event.location ?? '').trim();
  const suffix = `${LOCATION_SEPARATOR}${location}`;
  return location && title.endsWith(suffix) ? title.slice(0, -suffix.length).trim() : title;
}

const sourceKeyOf = (event: GroupableEvent): string => (event.sourceKey ?? '').trim();

const subjectOf = (event: GroupableEvent): string => {
  const source = sourceKeyOf(event);
  if (source) return ['source', source, stateTitle(event)].join('\u0000');
  return [event.title, event.location, event.deviceName].map((v) => (v ?? '').trim()).join('\u0000');
};

const deviceOf = (event: GroupableEvent): string => (event.deviceName ?? '').trim();
const locationOf = (event: GroupableEvent): string => (event.location ?? '').trim();

const defaultRepeatsOnly = (event: GroupableEvent): boolean =>
  !actorOf(event) && (event.kind?.app ?? '').toLowerCase() === 'iot';

interface Cluster<E> {
  site: string;
  actor: string; // '' while no actor has joined
  /** Set on a repeats-only cluster: the one subject it may hold. Null = an ordinary cluster. */
  subject: string | null;
  chains: Set<string>;
  /** Producing apps of the members ('' = unknown). */
  apps: Set<string>;
  /** Doors/devices and locations of the members (the same-place rule). */
  devices: Set<string>;
  locations: Set<string>;
  members: { event: E; t: number }[];
  first: number;
  last: number;
}

export function groupEvents<E extends GroupableEvent>(
  events: readonly E[],
  options: GroupEventsOptions<E> = {},
): EventListItem<E>[] {
  const gapMs = options.gapMs ?? DEFAULT_GAP_MS;
  const maxSpanMs = options.maxSpanMs ?? DEFAULT_MAX_SPAN_MS;
  const siteOf = (e: E) => String(options.getSite?.(e) ?? '');
  const repeatsOnly = options.isRepeatsOnly ?? defaultRepeatsOnly;

  // Oldest first so "previous event in the group" is the cluster's tail.
  const timed = events
    .map((event) => ({ event, t: new Date(event.time).getTime() }))
    .sort((a, b) => a.t - b.t || a.event.id - b.event.id);

  const clusters: Cluster<E>[] = [];
  let open: Cluster<E>[] = [];

  for (const item of timed) {
    const site = siteOf(item.event);
    const actor = actorOf(item.event);
    const chain = options.getChainKey?.(item.event) || '';
    const subject = repeatsOnly(item.event) ? subjectOf(item.event) : null;
    const app = (item.event.kind?.app ?? '').toLowerCase();
    // Unknown app on either side = app-blind (old behaviour); otherwise the app must already be there.
    const sameSource = (c: Cluster<E>) => !app || c.apps.has(app) || c.apps.has('');
    const device = deviceOf(item.event);
    const location = locationOf(item.event);
    const hasPlace = !!device || !!location;
    // A person's group and an actor-less event meet only at the same door/device or location.
    const samePlace = (c: Cluster<E>) => (!!device && c.devices.has(device)) || (!!location && c.locations.has(location));

    // Drop clusters that can no longer accept anything at or after `t`.
    open = open.filter((c) => item.t - c.last <= gapMs);

    let target: Cluster<E> | undefined;
    if (chain) {
      // A chain member rejoins its chain's group past the gap and whoever acted (within the span).
      target = nearest(
        clusters.filter((c) => c.site === site && c.chains.has(chain) && item.t - c.first <= maxSpanMs),
        item.t,
      );
    }
    if (!target && subject !== null) {
      // Repeats only: the open cluster of exactly this subject, nothing else.
      target = nearest(
        open.filter((c) => c.site === site && c.subject === subject && item.t - c.first <= maxSpanMs),
        item.t,
      );
    } else if (!target) {
      const eligible = open.filter((c) => c.site === site && c.subject === null && item.t - c.first <= maxSpanMs);
      if (actor) {
        // Same person first; otherwise adopt the nearest actor-less cluster.
        target =
          nearest(eligible.filter((c) => c.actor === actor), item.t) ??
          nearest(eligible.filter((c) => c.actor === '' && sameSource(c) && samePlace(c)), item.t);
      } else {
        // Same rule between two actor-less events: without place data of its own there's
        // nothing to contradict, so it still joins the nearest actor-less cluster; with
        // place data, same-place applies exactly as it does for a person's group.
        target = nearest(
          eligible.filter((c) => sameSource(c) && ((c.actor === '' && !hasPlace) || samePlace(c))),
          item.t,
        );
      }
    }

    if (target) {
      target.members.push(item);
      target.last = item.t;
      if (actor && !target.actor) target.actor = actor;
      if (chain) target.chains.add(chain);
      target.apps.add(app);
      if (device) target.devices.add(device);
      if (location) target.locations.add(location);
      if (!open.includes(target)) open.push(target);
    } else {
      const cluster: Cluster<E> = {
        site,
        actor,
        subject,
        chains: new Set(chain ? [chain] : []),
        apps: new Set([app]),
        devices: new Set(device ? [device] : []),
        locations: new Set(location ? [location] : []),
        members: [item],
        first: item.t,
        last: item.t,
      };
      clusters.push(cluster);
      open.push(cluster);
    }
  }

  const out: { item: EventListItem<E>; sortT: number; sortId: number }[] = [];
  for (const c of clusters) {
    const newestFirst = [...c.members].reverse();
    const newest = newestFirst[0];
    if (c.members.length < 2) {
      out.push({
        item: { kind: 'single', key: `e-${newest.event.id}`, event: newest.event },
        sortT: newest.t,
        sortId: newest.event.id,
      });
      continue;
    }
    // A person's group is headed by their own events; actor-less members only head actor-less groups.
    const withActor = newestFirst.filter((m) => actorOf(m.event));
    const candidates = withActor.length > 0 ? withActor : newestFirst;
    let headline = candidates[0];
    for (const m of candidates) {
      if (severityRank(m.event.severity) > severityRank(headline.event.severity)) headline = m;
    }
    const actors: string[] = [];
    for (const m of c.members) {
      const a = actorOf(m.event);
      if (a && !actors.includes(a)) actors.push(a);
    }
    out.push({
      item: {
        kind: 'group',
        key: `g-${c.members[0].event.id}`,
        events: newestFirst.map((m) => m.event),
        headline: headline.event,
        actors,
        start: new Date(c.first),
        end: new Date(c.last),
        ...(zoneOf(c.subject, c.members.map((m) => m.event))),
      },
      sortT: newest.t,
      sortId: newest.event.id,
    });
  }

  out.sort((a, b) => b.sortT - a.sortT || b.sortId - a.sortId);
  return out.map((o) => o.item);
}

/** The `zone` summary of a source-keyed repeats-only cluster, else nothing. */
function zoneOf<E extends GroupableEvent>(subject: string | null, members: E[]): { zone?: EventGroupZone } {
  const first = members[0];
  const sourceKey = sourceKeyOf(first);
  if (subject === null || !sourceKey) return {};
  const locations: string[] = [];
  for (const e of members) {
    const loc = locationOf(e);
    if (loc && !locations.includes(loc)) locations.push(loc);
  }
  const sourceName = members.map((e) => (e.sourceName ?? '').trim()).find(Boolean) ?? null;
  return { zone: { sourceKey, sourceName, state: stateTitle(first), locations } };
}

/** Header title of a group: `<state> — <zone>` for one zone across floors, else the headline's title. */
export function groupTitle<E extends GroupableEvent>(group: EventGroupItem<E>): string {
  const { zone } = group;
  if (!zone) return group.headline.title;
  return zone.sourceName ? `${zone.state}${LOCATION_SEPARATOR}${zone.sourceName}` : zone.state;
}

/** Header count of a group: `5 floors` for one zone across several floors, else `N events`. */
export function groupCountLabel<E extends GroupableEvent>(group: EventGroupItem<E>): string {
  const floors = group.zone?.locations.length ?? 0;
  return floors >= 2 ? `${floors} floors` : `${group.events.length} events`;
}

/** Header place of a group: the headline's door/device or location; none for a zone (its floors are the members). */
export function groupWhere<E extends GroupableEvent>(group: EventGroupItem<E>): string | null {
  if (group.zone && group.zone.locations.length >= 2) return null;
  return deviceOf(group.headline) || locationOf(group.headline) || null;
}

/** A member's line inside a group: its floor for one zone across floors (the header names the state), else null. */
export function memberLabel<E extends GroupableEvent>(group: EventGroupItem<E>, event: E): string | null {
  if (!group.zone || group.zone.locations.length < 2) return null;
  return locationOf(event) || null;
}

function nearest<E>(candidates: Cluster<E>[], t: number): Cluster<E> | undefined {
  let best: Cluster<E> | undefined;
  for (const c of candidates) {
    if (!best || t - c.last < t - best.last) best = c;
  }
  return best;
}
